import React, { useEffect, useMemo, useState } from 'react';
import { Gavel, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useApi, useAuth } from '@/contexts/AuthContext';

const C = { navy: '#0B1F3A', blue: '#1A4C7C', slate: '#5B6B7F', green: '#1B7F4B', amber: '#B7791F', red: '#B23A3A', bg: '#F4F6F9' };
const E = ['E1', 'E2', 'E3', 'E4'];
const T = ['T1', 'T2', 'T3', 'T4', 'T5'];
const num = v => (v === '' || v === null || v === undefined ? NaN : Number(v));

export const eqBand = (pga, cuts) => { if (pga === null || pga === undefined) return null; for (let i = 0; i < 3; i += 1) if (pga < cuts[i]) return E[i]; return 'E4'; };
export const theftBand = (p, scheme, cuts) => {
  if (scheme === 'fixed_rate' && p.rate_per_100k !== null && p.rate_per_100k !== undefined) { for (let i = 0; i < 4; i += 1) if (p.rate_per_100k < cuts[i]) return T[i]; return 'T5'; }
  return p.quintile_band || null;
};
const count = (items, fn, bands) => { const c = Object.fromEntries(bands.map(b => [b, 0])); let none = 0; items.forEach(x => { const b = fn(x); if (b) c[b] += 1; else none += 1; }); return { ...c, none }; };
const pct = (n, total) => (total ? ` (${Math.round((n / total) * 100)}%)` : '');

function Cell({ today, proposed, total, testId }) {
  const moved = today !== proposed;
  return (
    <td className="py-1 pr-2 font-mono" data-testid={testId}>
      <span style={{ color: C.slate }}>{today}{pct(today, total)}</span>
      <span className="mx-1" style={{ color: C.slate }}>→</span>
      <span className="font-semibold" style={{ color: moved ? C.amber : C.navy }}>{proposed}{pct(proposed, total)}</span>
    </td>
  );
}

function EqTable({ points, factors, cur, prop }) {
  const live = points.filter(p => !p.unresolved);
  const today = count(live, p => eqBand(p.pga_10, cur), E);
  const next = count(live, p => eqBand(p.pga_10, prop), E);
  const range = i => (i === 0 ? `< ${prop[0]} g` : i === 3 ? `≥ ${prop[2]} g` : `${prop[i - 1]} – ${prop[i]} g`);
  return (
    <table className="w-full text-[11px] mt-2" data-testid="band-eq-table">
      <thead><tr style={{ color: C.slate }}><th className="text-left py-1">Band</th><th className="text-left py-1">PGA 10 % / 50 yr</th><th className="text-left py-1">Factor</th><th className="text-left py-1">Book today → proposed</th></tr></thead>
      <tbody>
        {E.map((b, i) => (
          <tr key={b} className="border-t" style={{ borderColor: 'rgba(136,150,168,0.15)' }}>
            <td className="py-1 pr-2 font-semibold" style={{ color: C.navy }}>{b}</td>
            <td className="py-1 pr-2 font-mono" style={{ color: C.slate }}>{range(i)}</td>
            <td className="py-1 pr-2 font-mono" style={{ color: C.slate }}>×{factors?.levels?.[b]}</td>
            <Cell today={today[b]} proposed={next[b]} total={live.length} testId={`band-eq-${b}`} />
          </tr>
        ))}
        <tr className="border-t" style={{ borderColor: 'rgba(136,150,168,0.15)', color: C.slate }}><td className="py-1" colSpan={3}>no grid point within 15 km</td><td className="py-1 font-mono" data-testid="band-eq-none">{next.none}</td></tr>
      </tbody>
    </table>
  );
}

function TheftTable({ points, councils, factors, cur, prop }) {
  const live = points.filter(p => !p.unresolved);
  const today = count(live, p => theftBand(p, cur.theft_scheme, cur.theft_cutpoints_per_100k || []), T);
  const next = count(live, p => theftBand(p, prop.scheme, prop.cuts), T);
  const perState = Object.fromEntries(Object.entries(councils || {}).map(([s, v]) => [s, count(v.rows || [], r => theftBand(r, prop.scheme, prop.cuts), T)]));
  const def = i => (prop.scheme === 'fixed_rate' ? (i === 0 ? `< ${prop.cuts[0]}` : i === 4 ? `≥ ${prop.cuts[3]}` : `${prop.cuts[i - 1]} – ${prop.cuts[i]}`) + ' /100k' : ['lowest fifth', 'second fifth', 'middle fifth', 'fourth fifth', 'highest fifth'][i] + ' in the state');
  return (
    <table className="w-full text-[11px] mt-2" data-testid="band-theft-table">
      <thead><tr style={{ color: C.slate }}><th className="text-left py-1">Band</th><th className="text-left py-1">Council rate</th><th className="text-left py-1">Factor</th><th className="text-left py-1">Councils NSW / VIC / QLD</th><th className="text-left py-1">Book today → proposed</th></tr></thead>
      <tbody>
        {T.map((b, i) => (
          <tr key={b} className="border-t" style={{ borderColor: 'rgba(136,150,168,0.15)' }}>
            <td className="py-1 pr-2 font-semibold" style={{ color: C.navy }}>{b}</td>
            <td className="py-1 pr-2 font-mono" style={{ color: C.slate }}>{def(i)}</td>
            <td className="py-1 pr-2 font-mono" style={{ color: C.slate }}>×{factors?.levels?.[b]}</td>
            <td className="py-1 pr-2 font-mono" style={{ color: C.slate }} data-testid={`band-theft-councils-${b}`}>{['NSW', 'VIC', 'QLD'].map(s => perState[s]?.[b] ?? 0).join(' / ')}</td>
            <Cell today={today[b]} proposed={next[b]} total={live.length} testId={`band-theft-${b}`} />
          </tr>
        ))}
        <tr className="border-t" style={{ borderColor: 'rgba(136,150,168,0.15)', color: C.slate }}><td className="py-1" colSpan={4}>no council table (TAS, SA, WA, NT, ACT) — assumed T3</td><td className="py-1 font-mono" data-testid="band-theft-none">{next.none}</td></tr>
      </tbody>
    </table>
  );
}

// The owner's ruling on the T-band and E-band cutpoints, beside the book and the council tables.
export function BandRulingsPanel({ onRuled }) {
  const api = useApi();
  const { user } = useAuth();
  const isOwner = user?.role === 'platform_owner';
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [eq, setEq] = useState(['', '', '']);
  const [scheme, setScheme] = useState('within_state_quintile');
  const [tc, setTc] = useState(['', '', '', '']);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get('/jobs/job-a/bands').then(r => {
      setData(r.data);
      const ru = r.data.rulings;
      setEq(ru.eq_cutpoints_g.map(String)); setScheme(ru.theft_scheme); setTc((ru.theft_cutpoints_per_100k || [100, 200, 400, 800]).map(String));
    }).catch(e => setErr(e?.response?.data?.detail || 'The bands could not be loaded'));
  }, [api]);
  const propEq = useMemo(() => eq.map(num), [eq]);
  const propTc = useMemo(() => tc.map(num), [tc]);
  const eqOk = propEq.every(v => v > 0 && v < 1) && propEq[0] < propEq[1] && propEq[1] < propEq[2];
  const tcOk = scheme !== 'fixed_rate' || (propTc.every(v => v > 0) && propTc[0] < propTc[1] && propTc[1] < propTc[2] && propTc[2] < propTc[3]);
  if (err) return <p className="text-[11px] mt-3" style={{ color: C.red }} data-testid="band-rulings-error">{err}</p>;
  if (!data) return <p className="text-[11px] mt-3 flex items-center gap-1" style={{ color: C.slate }} data-testid="band-rulings-loading"><Loader2 className="w-3 h-3 animate-spin" />reading the book against the tables…</p>;
  const { rulings, factors, councils, book } = data;
  const ruled = rulings.status === 'ruled';
  const record = () => {
    const reason = window.prompt('Record this ruling on the band cutpoints — why these lines?');
    if (reason === null) return;
    if (reason.trim().length < 10) { toast.error('A reason of at least ten characters is required'); return; }
    setBusy(true);
    api.post('/jobs/job-a/bands/ruling', { eq_cutpoints_g: propEq, theft_scheme: scheme, theft_cutpoints_per_100k: scheme === 'fixed_rate' ? propTc : null, reason: reason.trim() })
      .then(r => { toast.success('Ruling recorded — audited; applies at the next hazards lookup'); setData(d => ({ ...d, rulings: r.data })); onRuled?.(); })
      .catch(e => toast.error(e?.response?.data?.detail || 'Not recorded')).finally(() => setBusy(false));
  };
  const withdraw = () => {
    const reason = window.prompt('Withdraw the ruling — the build\'s inferred cutpoints apply again, marked [I]. Why?');
    if (reason === null) return;
    if (reason.trim().length < 10) { toast.error('A reason of at least ten characters is required'); return; }
    setBusy(true);
    api.post('/jobs/job-a/bands/ruling/withdraw', { reason: reason.trim() })
      .then(r => { toast.success('Ruling withdrawn — audited'); setData(d => ({ ...d, rulings: r.data })); setEq(r.data.eq_cutpoints_g.map(String)); setScheme(r.data.theft_scheme); onRuled?.(); })
      .catch(e => toast.error(e?.response?.data?.detail || 'Not withdrawn')).finally(() => setBusy(false));
  };
  const inp = (val, set, i, step, testId) => <input type="number" step={step} min="0" value={val[i]} onChange={e => set(a => a.map((x, j) => (j === i ? e.target.value : x)))} className="w-20 text-[11px] border rounded-sm px-1 py-0.5 mx-1 bg-white font-mono" data-testid={testId} />;
  return (
    <div className="mt-3 rounded-sm p-3" style={{ background: C.bg }} data-testid="band-rulings-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[11px] font-semibold flex items-center gap-1" style={{ color: C.navy }}><Gavel className="w-3 h-3" />Band rulings — the cutpoints beside the book</p>
          <p className="text-[11px] mt-0.5" style={{ color: ruled ? C.green : C.amber }} data-testid="band-rulings-status">
            {ruled ? <>Ruled by {rulings.ruled_by} on {rulings.ruled_at?.slice(0, 10)} — “{rulings.reason}”</> : <>Interim [I] — the build's inferred cutpoints apply; no ruling recorded yet</>}
          </p>
          <p className="text-[10px] mt-0.5 font-mono" style={{ color: C.slate }} data-testid="band-rulings-book">
            book = {book.addresses} addresses quoted so far ({book.quotes.toLocaleString()} quotes{book.unresolved ? `, ${book.unresolved} unresolved` : ''}) · councils {['NSW', 'VIC', 'QLD'].map(s => `${s} ${councils[s]?.rows?.length ?? 0}`).join(' / ')} · factors {factors.EQ_ZONE?.indicative ? '[I]' : 'ruled'}
          </p>
        </div>
        {isOwner && (
          <div className="flex items-center gap-2">
            {ruled && <button onClick={withdraw} disabled={busy} className="px-3 py-1.5 rounded-sm text-[11px] font-semibold border disabled:opacity-50" style={{ color: C.red, borderColor: 'rgba(178,58,58,0.3)' }} data-testid="band-rulings-withdraw">Withdraw ruling</button>}
            <button onClick={record} disabled={busy || !eqOk || !tcOk} className="px-3 py-1.5 rounded-sm text-[11px] font-semibold text-white flex items-center gap-1 disabled:opacity-50" style={{ background: C.navy }} data-testid="band-rulings-record">
              <Gavel className="w-3 h-3" />Record ruling
            </button>
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-2">
        <div>
          <p className="text-[11px]" style={{ color: C.navy }}>E-band · NSHA23 PGA cutpoints (g): E1 &lt;{inp(eq, setEq, 0, '0.005', 'band-eq-cut-0')} E2 &lt;{inp(eq, setEq, 1, '0.005', 'band-eq-cut-1')} E3 &lt;{inp(eq, setEq, 2, '0.005', 'band-eq-cut-2')} else E4
            {!eqOk && <span className="ml-2" style={{ color: C.red }} data-testid="band-eq-invalid">ascending, between 0 and 1 g</span>}</p>
          <EqTable points={book.points} factors={factors.EQ_ZONE} cur={rulings.eq_cutpoints_g} prop={eqOk ? propEq : rulings.eq_cutpoints_g} />
        </div>
        <div>
          <p className="text-[11px]" style={{ color: C.navy }}>T-band · scheme
            <select value={scheme} onChange={e => setScheme(e.target.value)} className="text-[11px] border rounded-sm px-1 py-0.5 mx-1 bg-white" data-testid="band-theft-scheme">
              <option value="within_state_quintile">within-state quintile</option><option value="fixed_rate">fixed rate per 100k</option>
            </select>
            {scheme === 'fixed_rate' && <>T1 &lt;{inp(tc, setTc, 0, '10', 'band-theft-cut-0')} T2 &lt;{inp(tc, setTc, 1, '10', 'band-theft-cut-1')} T3 &lt;{inp(tc, setTc, 2, '10', 'band-theft-cut-2')} T4 &lt;{inp(tc, setTc, 3, '10', 'band-theft-cut-3')} else T5</>}
            {!tcOk && <span className="ml-2" style={{ color: C.red }} data-testid="band-theft-invalid">ascending, above 0</span>}</p>
          <TheftTable points={book.points} councils={councils} factors={factors.THEFT_ZONE} cur={rulings} prop={{ scheme, cuts: tcOk ? propTc : (rulings.theft_cutpoints_per_100k || []) }} />
        </div>
      </div>
      <p className="text-[10px] mt-2" style={{ color: C.slate }}>A ruling applies at the next hazards lookup — quotes already issued keep the bands they were priced on. The factors (×) are the rating standard's and stay [I] until the standard is ruled on separately.</p>
    </div>
  );
}
